import { useRef, type ChangeEvent } from 'react';

import type { VenueBookingPhoto } from '../../../../../api/venues';

import { ACCEPT_ATTRIBUTE } from './photo-files';
import { PhotoActions } from './PhotoActions';
import { PhotoSpecs } from './PhotoSpecs';
import type { PhotoLayout } from './use-photo-layout';
import type { VenuePhotoFlow } from './use-venue-photo-flow';

export interface PhotoCardControlsProps {
  source: VenueBookingPhoto['source'];
  layout: PhotoLayout;
  flow: VenuePhotoFlow;
}

/** The buttons, the hidden file picker and the size rules under the photo. */
export function PhotoCardControls({ source, layout, flow }: PhotoCardControlsProps) {
  const picker = useRef<HTMLInputElement>(null);
  const { phase } = flow;
  const showActions = phase === 'idle' || phase === 'reading' || phase === 'uploading';

  const onPicked = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (file) void flow.choose(file);
  };

  return (
    <>
      {showActions && (
        <>
          <PhotoActions
            source={source}
            disabled={phase !== 'idle'}
            phone={layout.phone}
            buttonHeight={layout.buttonHeight}
            focusRequested={flow.focusTarget === 'actions'}
            onFocused={flow.focusDone}
            onChoose={() => picker.current?.click()}
            onRemove={flow.askToRemove}
          />
          <input
            ref={picker}
            type="file"
            accept={ACCEPT_ATTRIBUTE}
            aria-label="Choose a booking photo"
            onChange={onPicked}
            style={{ display: 'none' }}
          />
        </>
      )}
      <PhotoSpecs showDropHint={!layout.phone} />
    </>
  );
}
